import React from 'react';
import { Typography } from 'antd';
import {
  useFormWatch,
  FieldArrayProvider,
  getValueFromFormState,
  useFormControllerState,
  useFieldArrayState,
} from '../../../../../../helpers/formManager';
import { fieldPathBuilders } from '../../../../shared/configs/acceptanceForm';
import {
  intervalTypeIds,
  intervalTypesMap,
} from '../../../../shared/configs/selectionData/intervalTypes';
import { ClassicSetupIntervals } from '../../../../controllers/ClassicSetupIntervals';
import SelectIntervalAcceptanceType from '../../../../controllers/SelectIntervalAcceptanceType';
import SelectIntervalAvailability from '../../../../controllers/SelectIntervalAvailability';
import ByIntervalSetupIntervals from '../../../../controllers/ByIntervalSetupIntervals';
import { DeskEntityProvider, useDeskEntity } from './deskEntityProvider';
import { useCreateIntervalIsImmutableFn } from '../../hooks/isImmutableFns';

const {
  active: createActiveName,
  mode: createModeName,
  availability: createAvailabilityName,
  duration: createIntervalDurationName,
  intervalId: createIntervalIdName,
} = fieldPathBuilders.scheduleSettingInterval;

const {
  start: createStartName,
  end: createEndName,
  duration: createDurationName,
  intervals: createIntervalsName,
} = fieldPathBuilders.deskScheduleSetting;

const { intervalType: createIntervalTypeName } = fieldPathBuilders.desk;

const rules = {
  required: 'Настройте интервалы',
  validate: (value) => {
    if (value?.every((i) => !i.active)) {
      return 'Активируйте хотя бы один интервал';
    }
  },
};

const IntervalList = ({ deskIndex, entityKey }) => {
  const intervalType = useFormWatch({
    name: createIntervalTypeName(deskIndex),
    exact: true,
  });

  if (!intervalTypeIds.includes(intervalType)) return null;

  const intervalsFieldName = createIntervalsName(deskIndex, entityKey);

  return (
    <FieldArrayProvider name={intervalsFieldName} rules={rules}>
      <DeskEntityProvider deskIndex={deskIndex} entityKey={entityKey}>
        <List />
        <ErrorBlock fieldName={intervalsFieldName} />
      </DeskEntityProvider>
    </FieldArrayProvider>
  );
};

export default IntervalList;

function ErrorBlock({ fieldName }) {
  const { errors } = useFormControllerState({ name: fieldName, exact: true });
  const errorData = getValueFromFormState(errors, fieldName);

  if (!errorData?.hasOwnProperty?.('root')) return null;

  return (
    <Typography.Text type="danger">{errorData.root.message}</Typography.Text>
  );
}

function List() {
  const [deskIndex, entityKey] = useDeskEntity();

  const intervalType = useFormWatch({
    name: createIntervalTypeName(deskIndex),
    exact: true,
  });

  const fields = useFieldArrayState();

  const intervalsName = createIntervalsName(deskIndex, entityKey);

  switch (intervalType) {
    case intervalTypesMap.classic: {
      return <ClassicIntervals fields={fields} intervalsName={intervalsName} />;
    }

    case intervalTypesMap.byInterval: {
      return <ByIntervalIntervals fields={fields} />;
    }

    default: {
      return null;
    }
  }
}

function ClassicIntervals({ fields, intervalsName }) {
  const [deskIndex, entityKey] = useDeskEntity();

  const observableNames = [
    createStartName(deskIndex, entityKey),
    createEndName(deskIndex, entityKey),
    createDurationName(deskIndex, entityKey),
  ];

  const intervalActiveToggleConfig = {
    createName: (index) => createActiveName(deskIndex, entityKey, index),
  };

  const isIntervalImmutable = useCreateIntervalIsImmutableFn(
    deskIndex,
    entityKey
  );

  return (
    <ClassicSetupIntervals
      observableNames={observableNames}
      fields={fields}
      intervalActiveToggleConfig={intervalActiveToggleConfig}
      renderIntervalSettings={RenderIntervalSettings}
      intervalsName={intervalsName}
      isIntervalDisabled={isIntervalImmutable}
    />
  );
}

function ByIntervalIntervals({ fields }) {
  const [deskIndex, entityKey] = useDeskEntity();

  const durationAndTimeConfig = {
    createDurationName: (index) =>
      createIntervalDurationName(deskIndex, entityKey, index),
    createIntervalIdName: (index) =>
      createIntervalIdName(deskIndex, entityKey, index),
  };

  const isIntervalImmutable = useCreateIntervalIsImmutableFn(
    deskIndex,
    entityKey
  );

  return (
    <ByIntervalSetupIntervals
      fields={fields}
      durationAndTimeConfig={durationAndTimeConfig}
      renderIntervalSettings={RenderIntervalSettings}
      isIntervalDisabled={isIntervalImmutable}
    />
  );
}

function RenderIntervalSettings({ index }) {
  const [deskIndex, entityKey] = useDeskEntity();

  return (
    <div className="intervalInnerFlexContainer">
      <SelectIntervalAcceptanceType
        name={createModeName(deskIndex, entityKey, index)}
      />
      <SelectIntervalAvailability
        name={createAvailabilityName(deskIndex, entityKey, index)}
      />
    </div>
  );
}
